// Las casillas de la quinta pantalla de «Qué te interesa»: una por cosa que se puede estudiar (R5).
//
//     node scripts/prueba_catalogo.mjs        (desde frontend/: van con los del catálogo)
//
// POR QUÉ NO ESTÁN ESCRITAS EN EL TEST. Gabri, 24 de octubre de 2026: *«sincronizado, para que
// al añadir un curso nuevo el test muestre su casilla»*. Escritas a mano serían una lista que
// alguien se olvida de tocar el día que se publica un curso. Salen del mismo catálogo que pinta
// `/cursos`, en el mismo momento.
//
// UNA CASILLA POR FAMILIA, NO POR CURSO, y esto también es suyo, el mismo día: *«si hay 6 cursos
// de inglés, se pone solo "Inglés". Si hay familias, como en lengua castellana, se pone
// directamente la familia, no los cursos. De este modo no solo pides menos cursos, sino que la
// información para el profesor resulta útil»*. Así que:
//   · los cursos de una familia —Inglés A1 a C2, Gramática I a VI— son una casilla, «Inglés»;
//   · y lo que se estudia en español —Gramática, Ortografía, Comunicación, Sociolingüística,
//     Educación literaria— es UNA, «Lengua y literatura castellana», que es su bloque;
//   · un curso que no cae en ninguna familia es su propia casilla, con su título: lo que no se
//     reconoce se enseña, que es la regla de `lib/catalogo.mjs`.
// La familia la decide `familiaDe()` por el título, la misma que agrupa las tarjetas del catálogo.
//
// CON SESIÓN, SÓLO LAS DE SU CURSO ESCOLAR: sale una casilla si alguno de sus cursos es de su año
// o no declara año. Y LA CLAVE ES EL NOMBRE SIN TILDES NI ESPACIOS (`ingles`,
// `lengua-y-literatura-castellana`): es lo que se guarda, lo que `/cursos` pone delante y lo que
// ve el profesor, siempre traducido de vuelta con el catálogo del día.
//
// Lógica pura, sin React y sin red.
import { BLOQUE_CATALANA, BLOQUE_LENGUA, familiaDe } from "../data/emojisDeFamilia.mjs";

/** La marca con la que una pregunta pide que la rellenen con el catálogo. */
export const DE_LOS_CURSOS = "cursos-publicados";

/** `ingles`, `lengua-y-literatura-castellana`: el nombre sin tildes, en minúsculas y con guiones. */
export const claveDe = (nombre) => String(nombre || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

/** Cómo se llama la casilla de un curso: su bloque si es de lengua castellana o catalana, si no su familia, y
 *  si no tiene familia, su título. */
export function grupoDe(titulo) {
  const f = familiaDe(titulo);
  if (f && f.bloque === BLOQUE_LENGUA) return BLOQUE_LENGUA;
  if (f && f.bloque === BLOQUE_CATALANA) return BLOQUE_CATALANA;
  return f ? f.familia : String(titulo || "");
}

/** Las casillas del catálogo publicado (cursos por competencia, como lo da el servidor): `[{clave, nombre, cursos}]`,
 *  por orden de nombre. Con `etapa`, sólo las que tienen algo de ese año (o sin año). */
export function gruposDelCatalogo(publicados, etapa = "") {
  const grupos = new Map();
  Object.values(publicados || {}).forEach((lista) => (lista || []).forEach((c) => {
    if (!c || !c.id || !c.titulo) return;
    const nombre = grupoDe(c.titulo);
    const clave = claveDe(nombre);
    if (!clave) return;
    const g = grupos.get(clave) || { clave, nombre, cursos: [], deSuAno: false };
    const etapas = c.etapa || [];
    g.cursos.push(c.id);
    g.deSuAno = g.deSuAno || !etapa || !etapas.length || etapas.includes(etapa);
    grupos.set(clave, g);
  }));
  return [...grupos.values()].filter((g) => g.deSuAno)
    .map(({ clave, nombre, cursos }) => ({ clave, nombre, cursos }))
    .sort((a, b) => a.nombre.localeCompare(b.nombre, "es", { numeric: true }));
}

/** Una opción por casilla. `marca` vacía: es una lista de casillas, no de letras. */
export function opcionesDeCursos(publicados, etapa = "") {
  return gruposDelCatalogo(publicados, etapa).map((g) => (
    { id: g.clave, label: g.nombre, marca: "", weights: { [`grupo:${g.clave}`]: 1 } }));
}

/** `{clave: nombre}` para traducir lo guardado: lo que ya no está publicado no tiene nombre. */
export function nombresDeGrupos(publicados) {
  return Object.fromEntries(gruposDelCatalogo(publicados).map((g) => [g.clave, g.nombre]));
}

/** Las preguntas del test con las de cursos ya rellenas. La que se queda sin ninguna, se va. */
export function conLosCursos(preguntas, publicados, etapa = "") {
  return (preguntas || []).flatMap((p) => {
    if (!p || p.opciones !== DE_LOS_CURSOS) return [p];
    const options = opcionesDeCursos(publicados, etapa);
    return options.length ? [{ ...p, options }] : [];
  });
}
